import React, {FC, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {Button, Checkbox, Form, Grid, Input, Space, Typography} from 'antd';
import {AuthFormContainer} from 'components/AuthFormContainer';
import {Spinner} from 'components/ui/Spinner';
import {getIsMobile} from 'helpers/utils';
import {observer} from 'mobx-react-lite';
import {AuthRoutePaths} from 'routing/Routes';
import {appState} from 'store/AppStore';
import {RegisterUserLocalRequest} from '../../../../../PRMGApiClient';

type SignUpFields = RegisterUserLocalRequest & {agreement?: boolean};

export const SignUpView: FC = observer((): JSX.Element => {
  const {t} = useTranslation();

  const screen = Grid.useBreakpoint();

  const isMobile = React.useMemo(() => getIsMobile(screen), [screen]);

  const onSubmit = useCallback((payload: SignUpFields) => {
    delete payload.agreement;
    appState.signUp(payload);
  }, []);

  return (
    <AuthFormContainer>
      <Space direction="vertical">
        <Typography.Title level={isMobile ? 2 : 1}>{t('Sign up')}</Typography.Title>
        <Typography.Text>{t('Please enter the details below to continue.')}</Typography.Text>
        <Form onFinish={onSubmit} layout="vertical" autoComplete="on">
          <Form.Item
            required
            label={t('Email')}
            name="email"
            rules={[{required: true, message: String(t('Email is required.'))}, {type: 'email'}]}
          >
            <Input type="email" placeholder="Email" id="new-username" name="new-username" autoComplete="new-username" />
          </Form.Item>
          <Form.Item
            name="password"
            required
            label={t('Password')}
            rules={[
              {required: true, message: String(t('Password is required.'))},
              {min: 8, message: String(t('Password must be at least 8 characters'))},
            ]}
          >
            <Input.Password
              id="new-password"
              autoComplete="new-password"
              name="new-password"
              type="password"
              placeholder={t('Password')}
            />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label={t('Confirm password')}
            dependencies={['password']}
            rules={[
              {
                required: true,
                message: String(t('Please confirm your password.')),
              },
              ({getFieldValue}) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password placeholder={t('Confirm password')} />
          </Form.Item>
          <Form.Item
            name="agreement"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error(t('Should accept agreement.'))),
              },
            ]}
          >
            <Checkbox>
              {t('I agree to the ')}
              <Button type="link" href={AuthRoutePaths.signIn.url} className="px-0 pr-1">
                {t('Terms of Service ')}
              </Button>
              {t('and ')}
              <Button type="link" href={AuthRoutePaths.signIn.url} className="px-0">
                {t('Privacy Policy*')}
              </Button>
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              {appState.isLoading ? <Spinner size="sm" /> : t('Sign up')}
            </Button>
            {t('Already have an account?')}{' '}
            <Link to={AuthRoutePaths.signIn.url}>
              <Button type="link" className="p-0">
                {t('Sign in!')}
              </Button>
            </Link>
          </Form.Item>
        </Form>
      </Space>
    </AuthFormContainer>
  );
});
